<script setup>
import { ref, onMounted } from 'vue';
import { message, Modal, Table, Card, Tag, Button, Input, Select, Space, Tooltip } from 'ant-design-vue';
import { getGrowthMemberLinks, sendGrowthMemberLineProbe } from '@/api/member/growthMemberLinks';
import { usePermission } from '@/composables/usePermission';

const { canView } = usePermission('GrowthMemberLinks');

const loading = ref(false);
const rows = ref([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(20);
const keyword = ref('');
const linkFilter = ref('all');

const filterOptions = [
    { label: '全部', value: 'all' },
    { label: '已對應成長（有 core）', value: 'linked' },
    { label: '僅本地帳號', value: 'local_only' },
    { label: '有 core 缺證件號', value: 'missing_id_number' },
    { label: '財務快照可用', value: 'snapshot_ready' },
    { label: '已對應但無快照', value: 'snapshot_empty' }
];

const statusLabel = (s) => {
    if (s === 1) return { text: '啟用', color: 'success' };
    if (s === -1) return { text: '封鎖', color: 'error' };
    return { text: '其他', color: 'default' };
};

const linkTagColor = (status) => {
    const map = {
        linked: 'success',
        missing_id_number: 'warning',
        id_only: 'processing',
        local_only: 'default'
    };
    return map[status] || 'default';
};

const columns = [
    { title: 'Gonghao ID', dataIndex: 'id', key: 'id', width: 96 },
    { title: '姓名', dataIndex: 'name', key: 'name', width: 120, ellipsis: true },
    { title: '身分證', dataIndex: 'id_number', key: 'id_number', width: 120, ellipsis: true },
    {
        title: 'core_member_id（成長）',
        dataIndex: 'core_member_id',
        key: 'core_member_id',
        width: 220,
        ellipsis: true
    },
    { title: '對應狀態', key: 'link', width: 170 },
    { title: '財務快照', key: 'snapshot', width: 160 },
    {
        title: '資產表',
        key: 'sheets',
        width: 130
    },
    { title: '來源', dataIndex: 'auth_source', key: 'auth_source', width: 88 },
    { title: '帳號狀態', key: 'status', width: 88 },
    { title: '更新時間', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    { title: '操作', key: 'actions', width: 120, fixed: 'right' }
];

const load = async (p = page.value) => {
    if (!canView.value) return;
    loading.value = true;
    try {
        const res = await getGrowthMemberLinks({
            page: p,
            page_size: pageSize.value,
            q: keyword.value?.trim() || undefined,
            link_filter: linkFilter.value === 'all' ? undefined : linkFilter.value
        });
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        const data = res.data || {};
        rows.value = data.items || [];
        total.value = data.total || 0;
        page.value = data.page || p;
        pageSize.value = data.page_size || pageSize.value;
    } catch (e) {
        message.error(e.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    page.value = 1;
    load(1);
};

const onTableChange = (pag) => {
    page.value = pag.current;
    pageSize.value = pag.pageSize;
    load(pag.current);
};

const probingId = ref(null);

const probeLine = (record) => {
    Modal.confirm({
        title: `模擬推播給 ${record.name || '這位會員'}`,
        content:
            '會送出兩則範例，格式與每天、每週日的正式推播相同（學員甲／學員乙），不是真實預約。對方若還沒加官方帳號好友，LINE 會拒絕。',
        okText: '送出測試',
        cancelText: '取消',
        async onOk() {
            probingId.value = record.id;
            try {
                const res = await sendGrowthMemberLineProbe(record.id);
                if (res.status !== 1) throw new Error(res.message || '模擬推播失敗');
                const detail = res.data?.detail || '已處理';
                if (res.data?.result === 'pushed') message.success(detail);
                else message.warning(detail);
            } catch (e) {
                message.error(e.message || '模擬推播失敗');
            } finally {
                probingId.value = null;
            }
        }
    });
};

const copyText = async (text) => {
    if (!text) return;
    try {
        await navigator.clipboard.writeText(String(text));
        message.success('已複製');
    } catch {
        message.warning('複製失敗');
    }
};

onMounted(() => load(1));
</script>

<template>
    <div class="growth-links-page pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>

        <template v-else>
            <Card
                :bordered="false"
                class="mb-4 shadow-sm rounded-2xl border border-emerald-100/50 bg-white"
            >
                <p class="text-sm text-[#5c6670] m-0 mb-3 leading-relaxed">
                    對照 gonghao 與世界樹文明會員（core_member_id、身分證）；財務快照僅含已完成的自行上傳版。模擬推播會送出與正式通知相同格式的今日、本週範例；沒加官方帳號好友時，即使有 line_id 也送不出去。
                </p>
                <div class="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <Input
                        v-model:value="keyword"
                        allow-clear
                        placeholder="搜尋姓名 / 證件 / core_member_id"
                        class="sm:max-w-xs"
                        @pressEnter="onSearch"
                    />
                    <Select
                        v-model:value="linkFilter"
                        :options="filterOptions"
                        class="w-full sm:w-[220px]"
                        @change="onSearch"
                    />
                    <Space>
                        <Button type="primary" @click="onSearch">查詢</Button>
                        <Button
                            @click="
                                () => {
                                    keyword = '';
                                    linkFilter = 'all';
                                    onSearch();
                                }
                            "
                        >
                            重置
                        </Button>
                    </Space>
                </div>
            </Card>

            <Card
                :bordered="false"
                class="shadow-md rounded-2xl border border-emerald-100/50 overflow-hidden bg-white"
            >
                <Table
                    :columns="columns"
                    :data-source="rows"
                    :loading="loading"
                    row-key="id"
                    size="middle"
                    :scroll="{ x: 1520 }"
                    :pagination="{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        showTotal: (t) => `共 ${t} 筆`
                    }"
                    :locale="{ emptyText: '尚無資料' }"
                    @change="onTableChange"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'id_number'">
                            <span v-if="record.id_number" class="font-mono text-sm">{{
                                record.id_number
                            }}</span>
                            <span v-else class="text-[#919191]">—</span>
                        </template>
                        <template v-else-if="column.key === 'core_member_id'">
                            <Tooltip v-if="record.core_member_id" :title="'點擊複製'">
                                <button
                                    type="button"
                                    class="core-id-btn font-mono text-xs text-left"
                                    @click="copyText(record.core_member_id)"
                                >
                                    {{ record.core_member_id }}
                                </button>
                            </Tooltip>
                            <span v-else class="text-[#919191]">—</span>
                        </template>
                        <template v-else-if="column.key === 'link'">
                            <Tag :color="linkTagColor(record.link_status)">
                                {{ record.link_label }}
                            </Tag>
                        </template>
                        <template v-else-if="column.key === 'snapshot'">
                            <Tag :color="record.snapshot_status === 'ready' ? 'success' : 'default'">
                                {{
                                    record.snapshot_status === 'ready'
                                        ? `可用 (${record.sheets_snapshot})`
                                        : '不可用'
                                }}
                            </Tag>
                        </template>
                        <template v-else-if="column.key === 'sheets'">
                            <span class="text-sm tabular-nums">
                                總 {{ record.sheets_total }} ／ 完成 {{ record.sheets_completed }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'status'">
                            <Tag :color="statusLabel(record.status).color">
                                {{ statusLabel(record.status).text }}
                            </Tag>
                        </template>
                        <template v-else-if="column.key === 'auth_source'">
                            <span class="text-sm">{{ record.auth_source || '—' }}</span>
                        </template>
                        <template v-else-if="column.key === 'actions'">
                            <Button
                                size="small"
                                :disabled="!record.core_member_id"
                                :loading="probingId === record.id"
                                @click="probeLine(record)"
                            >
                                模擬推播
                            </Button>
                        </template>
                    </template>
                </Table>
            </Card>
        </template>
    </div>
</template>

<style scoped>
.growth-links-page {
    max-width: 1400px;
}
.core-id-btn {
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: #8b6914;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}
.core-id-btn:hover {
    text-decoration: underline;
}
</style>
